import { FocusableOption, FocusMonitor, FocusOrigin  } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    AfterViewInit,
    OnDestroy,
    HostBinding,
    HostListener,
    Input,
    Inject,
    ElementRef,
    Optional,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ICC_MENU_PANEL } from './menu-panel';
import type { MenuPanel } from './menu-panel';
import { IccAppearance } from '../../enums/appearance';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[icc-menu-item]',
    exportAs: 'iccMenuItem',
    template: `<span class="content"><ng-content></ng-content></span>`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItemComponent implements FocusableOption, AfterViewInit, OnDestroy {
    private _disabled = false;

    @Input()
    @HostBinding('attr.data-appearance')
    appearance: IccAppearance | string = IccAppearance.Base;

    /** Menu item class */
    @HostBinding('class') class = 'menu-item';

    /** ARIA role for the menu item. */
    @HostBinding('attr.role')
    @Input()
    role: 'menuitem' = 'menuitem';

    @Input()
    @HostBinding('class.icc-disabled')
    get disabled() {
        return this._disabled;
    }
    set disabled(value: any) {
        this._disabled = coerceBooleanProperty(value);
    }

    @HostBinding('attr.disabled')
    get nativeDisabled(): '' | null {
        return this.disabled ? '' : null;
    }

    /** Menu item tab index */
    @HostBinding('attr.tabIndex') tabIndex = this.disabled ? '-1' : '0';

    /** Stream that emits when the menu item is focused. */
    readonly _focused = new Subject<MenuItemComponent>();


    @HostListener('click', ['$event.target']) 
    onClick() {
        this.setIndexItem();
    }

    constructor(
        private _elementRef: ElementRef<HTMLElement>,
        private _focusMonitor: FocusMonitor,
        @Inject(ICC_MENU_PANEL) @Optional() public _parentMenu?: MenuPanel<MenuItemComponent>
    ) {}

    /** Focuses the menu item. */
    focus(origin?: FocusOrigin, options?: FocusOptions): void {
        if (this._focusMonitor && origin) {
            this._focusMonitor.focusVia(this._getHostElement(), origin, options);
        } else {
            this._getHostElement().focus(options);
        }
        this.setIndexItem();
        this._focused.next(this);
    }

    setIndexItem(){
        if(this._parentMenu?.getChildIndex && this._parentMenu?.setIndexItem){
            const index = this._parentMenu.getChildIndex(this._getHostElement());
            this._parentMenu.setIndexItem(index);
        }
    }

    ngAfterViewInit() {
        if (this._focusMonitor) {
            // Start monitoring the element so it gets the appropriate focused classes. We want
            // to show the focus style for menu items only when the focus was not caused by a
            // mouse or touch interaction.
            this._focusMonitor.monitor(this._elementRef, false);
        }
    }

    ngOnDestroy() {
        if (this._focusMonitor) {
            this._focusMonitor.stopMonitoring(this._elementRef);
        }

        this._focused.complete();
    }

    /** Used to set the `tabindex`. */
    _getTabIndex(): string {
        return this.disabled ? '-1' : '0';
    }

    /** Returns the host DOM element. */
    _getHostElement(): HTMLElement {
        return this._elementRef.nativeElement;
    }

    getLabel(): string {
        const clone = this._elementRef.nativeElement.cloneNode(true) as HTMLElement;
        const icons = clone.querySelectorAll('icc-icon');

        // Strip away icons so they don't show up in the text.
        for (let i = 0; i < icons.length; i++) {
            const icon = icons[i];
            icon.parentNode?.removeChild(icon);
        }

        return clone.textContent?.trim() || '';
    }

}
